import { createContext, PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'

export const TransitionContext = createContext<undefined>(undefined)

const TransitionProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter()
  const gradient = useRef<HTMLDivElement>(null!)
  const sections = useRef<HTMLDivElement>(null!)
  const contextValue = useMemo(() => undefined, [])

  useEffect(() => {
    ScrollTrigger.clearScrollMemory('manual')

    router.beforePopState((state) => {
      // console.log(state, window.history.scrollRestoration)
      state.options.scroll = false
      return true
    })
  }, [router])

  return (
    <TransitionContext.Provider value={contextValue}>
      <main className={cn('static')}>
        <SwitchTransition mode="out-in">
          <Transition
            appear
            key={router.asPath}
            nodeRef={gradient}
            timeout={{
              enter: 0,
              exit: 800
            }}
            unmountOnExit
          >
            {(state) => (
              <div
                ref={gradient}
                className={cn(
                  'fixed left-0 top-0 z-10 h-screen w-full',
                  '[background:linear-gradient(45deg,rgba(0,0,0,0)_10%,_rgba(0,0,0,0.75)_25%,_rgba(0,0,0,1)_50%,_rgba(0,0,0,1)_65%,_rgba(0,0,0,0)_100%)]',
                  'transition-opacity duration-1000 ease-in-out-cubic',
                  'opacity-0',
                  {
                    'opacity-0': state === 'exiting',
                    'opacity-100':
                      (state === 'entering' || state === 'entered') && router.asPath !== '/'
                  }
                )}
              />
            )}
          </Transition>
        </SwitchTransition>

        <SwitchTransition mode="out-in">
          <Transition
            key={router.asPath}
            nodeRef={sections}
            timeout={{
              enter: 0,
              exit: 1400
            }}
            unmountOnExit
          >
            {(state) => (
              <div
                ref={sections}
                className={cn(
                  'relative z-20 h-auto w-full',
                  'transition-opacity duration-1000 ease-in-out-cubic',
                  'opacity-0',
                  {
                    'opacity-0': state === 'exiting',
                    'opacity-100': state === 'entering' || state === 'entered'
                  }
                )}
              >
                {children}
              </div>
            )}
          </Transition>
        </SwitchTransition>
      </main>
    </TransitionContext.Provider>
  )
}

export default TransitionProvider
